import React, { useState, useEffect } from 'react';
import { supabase_get_client_settings, supabase_update_client_settings, extractErrorMessage } from '../services/supabaseService';
import { Save, Loader2, Sparkles, MessageSquare, RotateCcw, AlertCircle, CheckCircle2, Bot, ChevronDown, ChevronUp } from 'lucide-react';

const OpenAILogo = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.28 9.82C22.49 8.53 21.91 7.22 20.81 6.5C19.72 5.77 18.29 5.76 17.18 6.46C16.89 5.16 15.93 4.1 14.65 3.66C13.38 3.22 11.95 3.45 10.9 4.28C9.85 3.45 8.42 3.22 7.14 3.66C5.86 4.1 4.9 5.16 4.61 6.46C3.5 5.76 2.07 5.77 0.98 6.5C-0.11 7.22 -0.69 8.53 -0.48 9.82C-1.59 10.52 -2.17 11.83 -1.96 13.13C-1.75 14.42 -0.79 15.48 0.49 15.92C0.78 17.22 1.74 18.28 3.02 18.72C4.29 19.16 5.72 18.93 6.77 18.1C7.82 18.93 9.25 19.16 10.53 18.72C11.81 18.28 12.77 17.22 13.06 15.92C14.17 16.62 15.6 16.61 16.69 15.88C17.78 15.16 18.36 13.85 18.15 12.56C19.26 11.86 19.84 10.55 19.63 9.25L22.28 9.82Z" fill="#10A37F"/>
    </svg>
);

const AzureLogo = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="9" height="9" fill="#F25022"/>
        <rect x="13" y="2" width="9" height="9" fill="#7FBA00"/>
        <rect x="2" y="13" width="9" height="9" fill="#00A4EF"/>
        <rect x="13" y="13" width="9" height="9" fill="#FFB900"/>
    </svg>
);

interface AiPromptsSettingsTabProps {
    clientId: string;
    user?: any;
}

export const AiPromptsSettingsTab: React.FC<AiPromptsSettingsTabProps> = ({ clientId, user }) => {
    const [prompts, setPrompts] = useState({
        // Bestaande prompts
        cvBuilderRewrite: '',
        persona: '',
        marketResearch: '',
        talentpoolMatch: '',
        buddyMotivatie: '',
        buddyCorrectie: '',
        buddyEisen: '',
        buddyExpertise: '',
        searchLogo: '',
        // Vacature-specifieke prompts
        vacatureGeneratie: '',
        vacatureAutoFill: '',
        vacatureIntro: '',
        vacatureFunctieomschrijving: '',
        vacatureWerkzaamheden: '',
        vacatureEisen: '',
        vacatureVoordelen: '',
        vacatureOverige: ''
    });
    const [provider, setProvider] = useState<'chatgpt' | 'azure'>('azure');
    const [apiKeys, setApiKeys] = useState({
        gemini: '',
        chatgpt: '',
        azure: '',
        kvk: ''
    });
    const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const isAdmin = user?.role === 'ADMIN' || user?.role === 'CLIENT';

    useEffect(() => {
        if (clientId) {
            fetchSettings();
        }
    }, [clientId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const settings = await supabase_get_client_settings(clientId);
            if (settings) {
                if (settings.ai_prompts_settings) {
                    setPrompts(prev => ({
                        ...prev,
                        ...settings.ai_prompts_settings
                    }));
                } else if (settings.ai_matching_settings?.customPrompt) {
                    // Fallback for migration from old AiMatching tab
                    setPrompts(prev => ({
                        ...prev,
                        talentpoolMatch: settings.ai_matching_settings.customPrompt
                    }));
                }
                const apiSettings = settings.ai_api_settings || {};
                const p = apiSettings.provider;
                setProvider(p === 'gemini' || p === 'google' || !p ? 'azure' : p === 'chatgpt' ? 'chatgpt' : 'azure');
                setApiKeys({
                    gemini: apiSettings.gemini || '',
                    chatgpt: apiSettings.chatgpt || '',
                    azure: apiSettings.azure || '',
                    kvk: apiSettings.kvk || ''
                });
            }
        } catch (e: any) {
            console.error('Error fetching AI prompts:', e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!clientId) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const settings = await supabase_get_client_settings(clientId);
            await supabase_update_client_settings(clientId, {
                ...settings,
                ai_prompts_settings: prompts,
                ai_api_settings: {
                    provider,
                    ...apiKeys
                }
            });
            setSuccess("AI Prompts Opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    const P_SECTIONS = [
        {
            key: 'cvBuilderRewrite',
            title: 'CV Builder: Werkervaring Herschrijven',
            icon: <Sparkles size={18} className="text-purple-500" />,
            placeholder: "Je bent 'Froukje', een AI recruiter. Herschrijf de onderstaande werkervaring.\nZorg dat alle bulletpoints beginnen met sterke, actieve werkwoorden in de ik-vorm (bijv. 'Implementeren van', 'Coördineren van').\nMaak het kort, bondig en resultaatgericht.",
            description: "Wordt gebruikt wanneer je in de CV Builder op de overkoepelende 'Herschrijf Alles' knop klikt."
        },
        {
            key: 'persona',
            title: 'Diepte Analyse: Persona Generatie',
            icon: <Bot size={18} className="text-emerald-500" />,
            placeholder: "Je bent een strategisch Recruitment Marketeer. Beschrijf de ideale persona voor deze vacature.\nGeef details over drijfveren, frustraties, de functionele werkstijl en noem veelvoorkomende functietitels of opstaprollen. Gebruik GEEN technische marketingtaal zoals 'Hybrid matrix'.",
            description: "Wordt toegepast in het tabblad 'Onderzoek & Persona' in een Vacature om de droomkandidaat te omschrijven."
        },
        {
            key: 'marketResearch',
            title: 'Diepte Analyse: Market Research',
            icon: <MessageSquare size={18} className="text-blue-500" />,
            placeholder: "Je bent expert in de Nederlandse arbeidsmarkt. Analyseer de kansen in de markt voor deze rol.\nBeoordeel de complexiteit van de vacature, schatting van de talentpool, concurrentie en geef 3 concrete zoeklocaties of concurrent-bedrijven aan.",
            description: "De opdracht instructie om in het Vacature Dashboard een arbeidsmarkt inschatting te genereren."
        },
        {
            key: 'talentpoolMatch',
            title: 'Talentpool Matching & AI Score',
            icon: <Sparkles size={18} className="text-amber-500" />,
            placeholder: "Opereer als een zeer kritische en seniore recruiter. Vergelijk dit CV met de vacature-eisen. Let extra goed op zachte factoren zoals loyaliteit en motivatie, maar penaliseer gaten in het CV of het ontbreken van een vereiste certificering hard.",
            description: "De achterliggende sturende prompt voor hoe de Froukje AI matches behaalt in je eigen Talentpool."
        },
        {
            key: 'buddyMotivatie',
            title: 'Motivatie Ombouw Buddy: Motivatie schrijven',
            icon: <Bot size={18} className="text-indigo-500" />,
            placeholder: "Schrijf 2 alinea's voor in een motivatiebrief op basis van de vacature en het CV. Begin niet elke zin met ik...",
            description: "Instructie voor de 'Alleen Motivatie' knop in de CV Builder."
        },
        {
            key: 'buddyCorrectie',
            title: 'Motivatie Ombouw Buddy: Tekst correctie',
            icon: <Bot size={18} className="text-indigo-500" />,
            placeholder: "Voer een tekstcorrectie uit. Verbeter grammatica, formatteer netjes, maar behoud de inhoud volledig.",
            description: "Instructie voor de 'Tekst Correctie' knop in de CV Builder."
        },
        {
            key: 'buddyEisen',
            title: 'Eisen/Wensen Ombouw Buddy: Toelichting',
            icon: <CheckCircle2 size={18} className="text-pink-500" />,
            placeholder: "Geef toelichting op de functie-eisen per eis in de ik-vorm. Geef concrete voorbeelden uit het verleden...",
            description: "Instructie voor de 'Toelichting Functie Eisen' knop."
        },
        {
            key: 'buddyExpertise',
            title: 'Competenties Ombouw Buddy: Expertise Bullets',
            icon: <Sparkles size={18} className="text-teal-500" />,
            placeholder: "Genereer een bullet list met expertisegebieden en competenties op basis van de vacature. Geen extra tekst.",
            description: "Instructie voor de 'Expertise Bullets' knop bij vaardigheden."
        },
        {
            key: 'searchLogo',
            title: 'Bedrijfsinfo: AI Zoek Logo',
            icon: <Bot size={18} className="text-orange-500" />,
            placeholder: "Vind de URL van het logo van het volgende bedrijf...",
            description: "Instructie voor het automatisch zoeken naar bedrijfslogo's."
        }
    ];

    const VACANCY_SECTIONS = [
        {
            key: 'vacatureGeneratie',
            title: 'Vacature Schrijven: Hoofdinstructie (Froukje Persona)',
            icon: <Sparkles size={18} className="text-rose-500" />,
            placeholder: `Je bent Froukje, een expert copywriter en recruiter. Schrijf een wervende vacaturetekst voor de rol "{jobTitle}".
Gebruik de marktcontext, persona en briefing om elke sectie overtuigend en professioneel in het Nederlands te schrijven.
Formatteer de inhoud in strakke HTML (gebruik <p>, <ul>, <li>, <strong> waar relevant). GEEN h1 of h2 tags.`,
            description: "De 'system persona' instructie die Froukje krijgt bij het genereren van de volledige vacaturetekst vanuit de Vacature Builder."
        },
        {
            key: 'vacatureAutoFill',
            title: 'Vacature Auto-aanvullen (vanuit Functietitel)',
            icon: <Bot size={18} className="text-indigo-500" />,
            placeholder: `Je bent een Senior Recruiter. Schrijf een volledige vacature intake op basis van een functietitel.
Jouw taak:
1. Bedenk een goede interne job code (bv. AFDELING-JAAR-001).
2. Bepaal het workplace type (On-site, Hybrid, Remote).
3. Schrijf een wervende beschrijving (Over de rol) met <p> tags.
4. Schrijf concrete eisen met <ul><li>...</li></ul>.
5. Schrijf aantrekkelijke voordelen met <ul><li>...</li></ul>.
6. Schat een marktconform salaris in.`,
            description: "Instructie die Froukje krijgt wanneer je op 'Auto-aanvullen' klikt in de vacature intake op basis van alleen de functietitel."
        },
        {
            key: 'vacatureIntro',
            title: 'Vacature Sectie: Intro / Over de rol',
            icon: <MessageSquare size={18} className="text-blue-500" />,
            placeholder: `Schrijf een aantrekkelijke, wervende introductie voor de vacature. Wek direct interesse.
Begin met een prikkelende zin over de organisatie of de uitdaging. Geen opsomming, alleen vloeiende alinea's van max 3 zinnen.
Gebruik <p> tags.`,
            description: "Extra instructie specifiek voor de 'Intro' of 'Over de rol' sectie van de gegenereerde vacaturetekst."
        },
        {
            key: 'vacatureEisen',
            title: 'Vacature Sectie: Eisen / Wat vragen wij',
            icon: <CheckCircle2 size={18} className="text-emerald-500" />,
            placeholder: `Genereer een concrete lijst van functie-eisen. Onderscheid HARDE eisen (must-have) van WENSEN (nice-to-have).
Gebruik altijd <ul><li>...</li></ul>. Begin elk punt met een sterk werkwoord of concept. Max 8 punten.`,
            description: "Extra instructie specifiek voor de 'Eisen' of 'Wat vragen wij' sectie van de vacaturetekst."
        },
        {
            key: 'vacatureVoordelen',
            title: 'Vacature Sectie: Voordelen / Wat bieden wij',
            icon: <Sparkles size={18} className="text-amber-500" />,
            placeholder: `Schrijf een aantrekkelijke lijst van arbeidsvoorwaarden en voordelen. Wees specifiek en vermijd clichés als 'marktconform salaris'.
Voeg altijd concrete details toe (bijv. 32-40 uur, hybride werken, laptop van de zaak).
Gebruik <ul><li>...</li></ul>.`,
            description: "Extra instructie specifiek voor de 'Voordelen' of 'Wat bieden wij' sectie van de vacaturetekst."
        },
        {
            key: 'vacatureFunctieomschrijving',
            title: 'Vacature Sectie: Functieomschrijving',
            icon: <MessageSquare size={18} className="text-cyan-500" />,
            placeholder: `Schrijf een gedetailleerde functieomschrijving. Wat is het doel van de rol en in welk team kom je terecht?
Gebruik <p> tags. Zorg dat de kandidaat precies begrijpt wat de essentie van de functie is.`,
            description: "Extra instructie specifiek voor de 'Functieomschrijving' sectie."
        },
        {
            key: 'vacatureWerkzaamheden',
            title: 'Vacature Sectie: Werkzaamheden / Taken',
            icon: <Bot size={18} className="text-purple-500" />,
            placeholder: `Wat gaat de kandidaat op een dagelijkse basis doen? Geef een concrete opsomming van verantwoordelijkheden en taken.
Gebruik altijd <ul><li>...</li></ul>. Maak het actief en resultaatgericht.`,
            description: "Extra instructie specifiek voor de 'Werkzaamheden' sectie."
        },
        {
            key: 'vacatureOverige',
            title: 'Vacature Sectie: Overige Informatie',
            icon: <Sparkles size={18} className="text-pink-500" />,
            placeholder: `Geef aan hoe het sollicitatieproces eruitziet en met wie contact opgenomen kan worden voor vragen.
Sluit af met een enthousiaste call-to-action.`,
            description: "Extra instructie specifiek voor de 'Overige informatie' sectie aan het einde van de vacature."
        }
    ];

    return (
        <div className="max-w-4xl space-y-8 animate-fadeIn pb-24">
            {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
            {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}

            <div className="flex items-center justify-between pb-6 border-b border-slate-200">
                <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">AI Prompt Instellingen</h2>
                    <p className="text-sm text-slate-500 font-medium mt-1 max-w-2xl">
                        Personaliseer de exacte instructies (prompts) die de AI krijgt voor specifieke handelingen binnen het platform. Zo klinkt Froukje of je output altijd zoals jouw bedrijfsidentiteit.
                    </p>
                    <div className="mt-4 flex items-center gap-3 bg-indigo-50 border border-indigo-100 px-4 py-2 rounded-xl w-fit cursor-pointer hover:bg-indigo-100/50 transition-colors">
                        <div className="w-9 h-5 bg-indigo-500 rounded-full flex items-center px-0.5 justify-end shadow-inner relative">
                            <div className="w-4 h-4 bg-white rounded-full shadow-sm absolute right-0.5" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs font-black tracking-wide text-indigo-900">Instellen voor iedereen van mijn bedrijf</span>
                            <span className="text-[10px] font-medium text-indigo-600">Deze prompts worden gedeeld met al je collega's</span>
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-slate-900 text-white text-xs font-bold uppercase tracking-widest rounded-lg hover:bg-black transition-colors flex items-center gap-2 shadow-md shadow-slate-200"
                >
                    {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Opslaan
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-8">
                <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2"><Bot size={20} className="text-indigo-500" /> AI Provider & API Configuratie</h3>
                <p className="text-xs text-slate-600 mb-4 rounded-lg bg-slate-50 border border-slate-100 p-3">
                    Standaard gebruikt de app <strong>Azure OpenAI</strong> via de backend (configureer <code className="text-[10px] bg-white px-1 rounded">server/.env</code>).
                    Optioneel: eigen <strong>OpenAI</strong>-sleutel voor specifieke Buddy-flows die direct OpenAI aanroepen.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <label className={`border rounded-xl p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${provider === 'azure' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300'}`}>
                        <input type="radio" name="provider" value="azure" checked={provider === 'azure'} onChange={() => isAdmin && setProvider('azure')} className="hidden" disabled={!isAdmin} />
                        <AzureLogo />
                        <span className="font-bold text-sm text-slate-800">Azure OpenAI (standaard)</span>
                        <span className="text-[10px] text-slate-400">Via server</span>
                    </label>
                    <label className={`border rounded-xl p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${provider === 'chatgpt' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300'}`}>
                        <input type="radio" name="provider" value="chatgpt" checked={provider === 'chatgpt'} onChange={() => isAdmin && setProvider('chatgpt')} className="hidden" disabled={!isAdmin} />
                        <OpenAILogo />
                        <span className="font-bold text-sm text-slate-800">OpenAI ChatGPT</span>
                        <span className="text-[10px] text-slate-400">Optioneel (Buddy)</span>
                    </label>
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-700 mb-1">OpenAI API Key <span className="text-[10px] font-normal text-slate-400">(alleen voor Buddy direct)</span></label>
                            <input type={isAdmin ? "text" : "password"} value={apiKeys.chatgpt} onChange={(e) => isAdmin && setApiKeys(p => ({ ...p, chatgpt: e.target.value }))} readOnly={!isAdmin} className="w-full p-2 text-sm border border-slate-200 rounded focus:border-indigo-400 outline-none font-mono" placeholder={isAdmin ? "sk-..." : "Verborgen voor niet-admins"} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-700 mb-1">KVK API Key <span className="text-[10px] text-slate-400 font-normal">(Bedrijfsinformatie)</span></label>
                            <input type={isAdmin ? "text" : "password"} value={apiKeys.kvk} onChange={(e) => isAdmin && setApiKeys(p => ({ ...p, kvk: e.target.value }))} readOnly={!isAdmin} className="w-full p-2 text-sm border border-slate-200 rounded focus:border-indigo-400 outline-none font-mono" placeholder={isAdmin ? "l7xx..." : "Verborgen voor niet-admins"} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Helper voor het renderen van een prompt sectie */}
            {[{ sections: P_SECTIONS, title: '🤖 Froukje & Platform Prompts', subtitle: 'CV Builder, Matching, Persona generatie en overige platform functies.' }, { sections: VACANCY_SECTIONS, title: '📋 Vacature Prompts — Per Onderdeel', subtitle: 'Stuur de AI-output bij voor het schrijven en genereren van vacatureteksten. Laat leeg = standaard Froukje schrijfstijl.' }].map(group => (
                <div key={group.title}>
                    <div className="flex items-center gap-3 mb-4">
                        <div>
                            <h3 className="text-base font-black text-slate-800">{group.title}</h3>
                            <p className="text-xs text-slate-400 font-medium">{group.subtitle}</p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        {group.sections.map(sec => (
                            <div key={sec.key} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm transition-all duration-200">
                                <div
                                    className="bg-slate-50 p-4 flex items-center justify-between border-b border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors"
                                    onClick={() => setExpandedPrompt(expandedPrompt === sec.key ? null : sec.key)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100">{sec.icon}</div>
                                        <div>
                                            <h4 className="text-sm font-black text-slate-900">{sec.title}</h4>
                                            <p className="text-xs text-slate-500 font-medium">{sec.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {(prompts as any)[sec.key] && (
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Aangepast</span>
                                        )}
                                        {expandedPrompt === sec.key ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                                    </div>
                                </div>

                                {expandedPrompt === sec.key && (
                                    <div className="p-6 bg-white animate-fadeIn">
                                        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 mb-4 font-mono leading-relaxed">
                                            <span className="font-black text-slate-700">Standaard prompt:</span><br />
                                            {sec.placeholder}
                                        </p>
                                        <label className="block text-xs font-bold text-slate-700 mb-2">Jouw aangepaste instructie (overschrijft standaard):</label>
                                        <textarea
                                            value={(prompts as any)[sec.key] || ''}
                                            onChange={(e) => setPrompts(p => ({ ...p, [sec.key]: e.target.value }))}
                                            placeholder={`Typ hier je eigen instructie...\n\nLaat leeg om de standaard te gebruiken.`}
                                            className="w-full h-40 p-4 bg-white border border-indigo-200 rounded-xl text-sm font-mono text-slate-700 leading-relaxed outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 transition-all resize-y shadow-inner"
                                        />
                                        <div className="flex justify-between items-center mt-3">
                                            <div className="flex items-center gap-2">
                                                <label className="text-[10px] font-bold text-slate-700">AI Provider voor deze prompt:</label>
                                                <select
                                                    value={(prompts as any)[`${sec.key}Provider`] || 'default'}
                                                    onChange={(e) => setPrompts(p => ({ ...p, [`${sec.key}Provider`]: e.target.value }))}
                                                    className="text-[10px] p-1.5 border border-slate-200 rounded font-medium focus:border-indigo-400 outline-none"
                                                >
                                                    <option value="default">Standaard (zoals algemeen ingesteld)</option>
                                                    <option value="azure">Azure OpenAI (server)</option>
                                                    <option value="chatgpt">OpenAI ChatGPT (direct)</option>
                                                </select>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <p className="text-[10px] text-slate-400">
                                                    Laat leeg voor <span className="font-bold">standaard Froukje instructies</span>.
                                                </p>
                                                <button
                                                    onClick={() => setPrompts(p => ({ ...p, [sec.key]: '', [`${sec.key}Provider`]: 'default' }))}
                                                    className="text-[10px] font-bold text-slate-400 hover:text-red-500 uppercase tracking-wider flex items-center gap-1"
                                                >
                                                    <RotateCcw size={10} /> Reset
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};
